import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

const TOKEN_KEY = "famfeel_admin_token";

function fmtDay(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * Activity pill helper. Returns a relative label ("active 2d ago",
 * "active just now") plus a tone bucket so the admin can scan a long
 * list and spot stale families. Tones map to colors:
 *   - "fresh" (≤ 1 day)   → green
 *   - "warm"  (≤ 7 days)  → neutral
 *   - "cool"  (≤ 30 days) → amber
 *   - "cold"  (> 30 days) → red
 *   - "never" (no activity ever) → muted red
 */
function activityPill(lastIso) {
  if (!lastIso) {
    return { label: "never used", tone: "never" };
  }
  let ms;
  try {
    ms = Date.now() - new Date(lastIso).getTime();
  } catch {
    return { label: "—", tone: "cool" };
  }
  if (Number.isNaN(ms) || ms < 0) ms = 0;
  const mins = Math.round(ms / 60000);
  const hrs = Math.round(ms / 3_600_000);
  const days = Math.round(ms / 86_400_000);
  let label;
  if (mins < 5) label = "active just now";
  else if (mins < 60) label = `active ${mins}m ago`;
  else if (hrs < 48) label = `active ${hrs}h ago`;
  else if (days < 30) label = `active ${days}d ago`;
  else label = `active ${Math.round(days / 30)}mo ago`;
  let tone;
  if (days <= 1) tone = "fresh";
  else if (days <= 7) tone = "warm";
  else if (days <= 30) tone = "cool";
  else tone = "cold";
  return { label, tone };
}

function activityPillClass(tone) {
  switch (tone) {
    case "fresh":
      return "bg-[#CDE6D2] border-[#9BC9A4] text-[#2A4A33]";
    case "warm":
      return "bg-[color:var(--bg-soft)] border-[color:var(--line-soft)] text-[color:var(--ink-2)]";
    case "cool":
      return "bg-[#FFE8A1]/70 border-[#D4A574]/50 text-[#5A4A2E]";
    case "cold":
      return "bg-[#F2C7BD] border-[#D49A8E] text-[#7a2e22]";
    case "never":
      return "bg-[#EADFDF] border-[#C9B5B5] text-[#7a2e22]";
    default:
      return "bg-[color:var(--bg-soft)] border-[color:var(--line-soft)] text-[color:var(--ink-2)]";
  }
}

// ISO-3166 two-letter code → flag emoji (regional indicator symbols)
function flagOf(code) {
  if (!code || code.length !== 2 || !/^[A-Z]{2}$/.test(code)) return "🌐";
  const A = 0x1f1e6 - 0x41;
  return String.fromCodePoint(code.charCodeAt(0) + A, code.charCodeAt(1) + A);
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);
  // Self-contained "all families" table — fetched once on mount and
  // refreshed after any admin action that mutates a family (delete,
  // tier override) so the row reflects the new state without a page
  // reload. Lives separately from `stats` because it's a different
  // shape and we want the dashboard to render even if the families
  // list happens to fail (e.g., timeout on a big install).
  const [families, setFamilies] = useState(null);
  const [familiesLoading, setFamiliesLoading] = useState(false);
  const navigate = useNavigate();

  const loadFamilies = useCallback(async (token) => {
    setFamiliesLoading(true);
    try {
      const res = await api.adminFamiliesList(token);
      setFamilies(res?.families || []);
    } catch {
      setFamilies([]);
    } finally {
      setFamiliesLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) {
      navigate("/admin", { replace: true });
      return;
    }
    api.adminStats(token)
      .then(setStats)
      .catch((e) => {
        if (e?.response?.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          navigate("/admin", { replace: true });
        } else {
          setErr("Couldn't load stats");
        }
      });
    loadFamilies(token);
  }, [navigate, loadFamilies]);

  const signOut = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    navigate("/admin", { replace: true });
  };

  // Nuke a family (test data, spam, abuse). Asks for an explicit typed
  // confirmation of the join_code so a misclick can't wipe the wrong
  // family — backend also re-validates `?confirm=` against the code.
  const [deleting, setDeleting] = useState(null); // holds join_code while in flight
  const deleteFamily = async (joinCode, name) => {
    const code = (joinCode || "").toUpperCase();
    if (!code) return;
    const typed = window.prompt(
      `This permanently deletes "${name}" and every member, check-in, message, event and chore inside it.\n\nType the join code ${code} to confirm:`,
    );
    if (!typed || typed.trim().toUpperCase() !== code) {
      if (typed != null) alert("Join code didn't match — nothing deleted.");
      return;
    }
    setDeleting(code);
    try {
      const token = sessionStorage.getItem(TOKEN_KEY);
      await api.adminDeleteFamily(token, code);
      // Refetch stats + families list so the row disappears and
      // counts reflect the new state immediately.
      const fresh = await api.adminStats(token);
      setStats(fresh);
      loadFamilies(token);
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't delete family");
    } finally {
      setDeleting(null);
    }
  };

  if (!stats && !err) {
    return (
      <div className="min-h-screen bg-[color:var(--bg,#F5EFE2)] flex items-center justify-center">
        <p className="font-display-italic text-2xl">Loading…</p>
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen bg-[color:var(--bg,#F5EFE2)] flex items-center justify-center">
        <p className="font-display-italic text-2xl">{err}</p>
      </div>
    );
  }

  const maxVisits = Math.max(1, ...(stats.daily || []).map((d) => d.visits));

  return (
    <div className="min-h-screen bg-[color:var(--bg,#F5EFE2)] text-[color:var(--ink)]">
      <header className="px-6 md:px-10 py-5 flex items-center justify-between border-b border-[color:var(--line-soft)]">
        <div>
          <div className="label-tiny">FamFeel · admin</div>
          <h1 className="font-display-italic text-3xl md:text-4xl tracking-tight">Dashboard</h1>
        </div>
        <button onClick={signOut} className="ff-btn" data-testid="admin-signout">
          Sign out
        </button>
      </header>

      <main className="px-6 md:px-10 py-10 max-w-6xl mx-auto space-y-10">
        <TierOverridePanel />

        <InactiveNudgePanel />

        <FamiliesTable
          families={families}
          loading={familiesLoading}
          onDelete={deleteFamily}
          deleting={deleting}
        />

        <FeedbackInbox />

        {/* Overview — four logical groupings instead of a flat 8-cell row. */}
        <div className="space-y-6">
          {/* Business: how big is FamFeel, how many paying. */}
          <section>
            <div className="label-tiny mb-2">Business</div>
            <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
              <Stat label="Families" value={stats.family_count} />
              <Stat label="Active subs" value={stats.active_subscriptions} />
            </div>
          </section>

          {/* Traffic: marketing site pulse. Split into landing-page
              (SEO / public) and in-app (logged-in families) so we can
              see which side of the funnel is moving on a given day. */}
          <section>
            <div className="label-tiny mb-2">Traffic — Landing (SEO)</div>
            <div className="grid grid-cols-2 gap-4">
              <Stat label="Visitors today" value={stats.visits_today} />
              <Stat label="Visitors total" value={stats.visits_total} />
            </div>
          </section>

          <section>
            <div className="label-tiny mb-2">Traffic — In-app</div>
            <div className="grid grid-cols-2 gap-4">
              <Stat label="App opens today" value={stats.app_visits_today ?? 0} />
              <Stat label="App opens total" value={stats.app_visits_total ?? 0} />
            </div>
          </section>

          {/* People: who's inside the families. */}
          <section>
            <div className="label-tiny mb-2">People in families</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="Members" value={stats.member_count} />
              <Stat label="Kids" value={stats.kid_count} />
              <Stat label="Pets" value={stats.pet_count} />
              <Stat label="Check-ins" value={stats.checkin_count} />
            </div>
          </section>

          {/* Dashboard mode split — how families are actually using FamFeel:
              calendar-front (the new default for new families), mood-front
              (check-in cards), or pets-front (care-log focus). Useful steer
              for what to build next. */}
          <section>
            <div className="label-tiny mb-2">Dashboard mode</div>
            <div className="grid grid-cols-3 gap-4">
              <Stat
                label="Mood front"
                value={stats.dashboard_modes?.full ?? 0}
              />
              <Stat
                label="Calendar front"
                value={stats.dashboard_modes?.calendar ?? 0}
              />
              <Stat
                label="Pets front"
                value={stats.dashboard_modes?.pets ?? 0}
              />
            </div>
          </section>
        </div>

        {/* Daily visitors bar chart */}
        <section>
          <div className="label-tiny mb-3">Daily visitors · last 30 days</div>
          <div className="ff-card p-5 md:p-6">
            {(() => {
              const daily = stats.daily || [];
              const totalVisits = daily.reduce((n, d) => n + (d.visits || 0), 0);
              const peak = daily.reduce(
                (best, d) => ((d.visits || 0) > (best?.visits || 0) ? d : best),
                null,
              );
              if (daily.length === 0 || totalVisits === 0) {
                return (
                  <p className="label-tiny text-[color:var(--ink-3)]">
                    No visits recorded yet.
                  </p>
                );
              }
              return (
                <>
                  {/* Headline: 30-day total + the busiest single day so the
                      chart card has a number you can read at a glance, not
                      just a wall of bars. */}
                  <div className="flex items-baseline justify-between gap-4 mb-4">
                    <div>
                      <div className="font-display text-3xl tabular leading-none tracking-tighter">
                        {totalVisits}
                      </div>
                      <div className="label-tiny text-[color:var(--ink-3)] mt-1">
                        visits · 30 days
                      </div>
                    </div>
                    {peak && (
                      <div className="text-right">
                        <div className="font-display text-xl tabular leading-none tracking-tighter">
                          {peak.visits}
                        </div>
                        <div className="label-tiny text-[color:var(--ink-3)] mt-1">
                          peak · {fmtDay(peak.date)}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-end gap-1 h-48" data-testid="visits-chart">
                    {daily.map((d) => {
                      const v = d.visits || 0;
                      const heightPct = (v / maxVisits) * 100;
                      const isPeak = peak && d.date === peak.date && v > 0;
                      return (
                        <div
                          key={d.date}
                          className="flex-1 min-w-[8px] flex flex-col items-end gap-1 group h-full justify-end"
                          title={`${fmtDay(d.date)} · ${v} visits · ${d.unique} unique`}
                        >
                          <div className="label-tiny text-[10px] text-[color:var(--ink-3)] opacity-0 group-hover:opacity-100 transition-opacity">
                            {v}
                          </div>
                          <div
                            className={`w-full rounded-t-md transition-all ${
                              isPeak
                                ? "bg-gradient-to-t from-[#A14231] to-[#D4A574]"
                                : "bg-gradient-to-t from-[#3A4759] to-[#7A8FA8] group-hover:from-[#2A3749] group-hover:to-[#9CB1CC]"
                            }`}
                            style={{ height: `${heightPct}%`, minHeight: v > 0 ? "4px" : "2px" }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between label-tiny text-[color:var(--ink-3)] mt-3">
                    <span>{daily[0] ? fmtDay(daily[0].date) : "—"}</span>
                    <span>today</span>
                  </div>
                </>
              );
            })()}
          </div>
        </section>

        {/* Top referrers */}
        <section>
          <div className="label-tiny mb-3">Top sources · last 30 days</div>
          <div className="ff-card p-5 md:p-6" data-testid="referrers-list">
            {(stats.referrers || []).length === 0 ? (
              <p className="label-tiny text-[color:var(--ink-3)]">No referrers yet.</p>
            ) : (
              <ul className="divide-y divide-[color:var(--line-soft)]">
                {stats.referrers.map((r) => (
                  <li key={r.source} className="flex items-center justify-between py-2.5 gap-3">
                    <span className="font-mono text-sm truncate">{r.source}</span>
                    <span className="font-display text-xl tracking-tighter">{r.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Family countries — self-reported at signup */}
        <section>
          <div className="label-tiny mb-3">Families · by country</div>
          <div className="ff-card p-5 md:p-6" data-testid="countries-list">
            {(stats.countries || []).length === 0 ? (
              <p className="label-tiny text-[color:var(--ink-3)]">No families yet.</p>
            ) : (
              <ul className="divide-y divide-[color:var(--line-soft)]">
                {stats.countries.map((c) => (
                  <li key={c.country} className="flex items-center justify-between py-2.5 gap-3">
                    <span className="flex items-center gap-3 min-w-0">
                      <span className="text-2xl leading-none">{flagOf(c.country)}</span>
                      <span className="font-mono text-sm">{c.country === "?" ? "Not shared" : c.country}</span>
                    </span>
                    <span className="font-display text-xl tracking-tighter">{c.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Families with member breakdown */}
        <section>
          <div className="label-tiny mb-3">All families</div>
          <div className="ff-card p-5 md:p-6" data-testid="families-list">
            {(stats.families || []).length === 0 ? (
              <p className="label-tiny text-[color:var(--ink-3)]">No families yet.</p>
            ) : (
              <ul className="divide-y divide-[color:var(--line-soft)]">
                {stats.families.map((f) => {
                  const act = activityPill(f.last_active_at);
                  return (
                  <li key={f.id} className="py-4 flex flex-col gap-2" data-testid={`family-row-${f.id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="font-display text-lg tracking-tight truncate">{f.name}</div>
                        <div className="label-tiny text-[color:var(--ink-3)] flex flex-wrap items-center gap-x-1.5 gap-y-1">
                          <span>
                            {(f.plan_tier || "free").toUpperCase()} · {fmtTime(f.created_at)} · {f.member_count} members · {f.kid_count} kids · {f.pet_count} pets
                          </span>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] tracking-normal normal-case font-medium ${activityPillClass(act.tone)}`}
                            data-testid={`family-active-${f.id}`}
                            title={
                              f.last_active_at
                                ? `Last activity: ${fmtTime(f.last_active_at)}`
                                : "No check-ins or events ever — likely abandoned signup"
                            }
                          >
                            {act.label}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span
                          className="font-mono text-sm tabular tracking-widest px-2 py-1 rounded-md bg-[color:var(--bg-soft)] text-[color:var(--ink)] border border-[color:var(--line-soft)]"
                          title="Family join code — use this + the family PIN to sign in"
                          data-testid={`family-join-code-${f.id}`}
                        >
                          {f.join_code || "—"}
                        </span>
                        <button
                          type="button"
                          onClick={() => deleteFamily(f.join_code, f.name)}
                          disabled={deleting === f.join_code}
                          className="ff-chip text-[12px] text-[color:var(--danger,#a14231)]"
                          data-testid={`admin-delete-family-${f.id}`}
                          title="Permanently delete this family and all its data"
                        >
                          {deleting === f.join_code ? "Deleting…" : "Remove"}
                        </button>
                      </div>
                    </div>
                    <div className="label-tiny text-[color:var(--ink-3)] font-mono">
                      id: {f.id.slice(0, 8)}…
                      {f.terms_accepted_at ? (
                        <span
                          className="ml-2 text-[color:var(--ink-2)]"
                          title={`Terms & Privacy accepted at ${f.terms_accepted_at}`}
                          data-testid={`family-terms-${f.id}`}
                        >
                          · T&amp;C ✓ {fmtTime(f.terms_accepted_at)}
                        </span>
                      ) : (
                        <span
                          className="ml-2 text-[color:var(--ink-3)] italic"
                          title="Family was created before the Terms checkbox was added"
                          data-testid={`family-terms-${f.id}`}
                        >
                          · T&amp;C —
                        </span>
                      )}
                    </div>
                    {f.members && f.members.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {f.members.map((m, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1.5 bg-[color:var(--bg-soft)] rounded-full px-2.5 py-1 label-tiny text-[color:var(--ink)]"
                            title={`${m.role}${m.age != null ? ` · ${m.age}` : ""}`}
                          >
                            <span>{m.avatar_emoji || (m.role === "pet" ? "🐾" : "👤")}</span>
                            <span className="font-mono">{m.name}</span>
                            {m.age != null && (
                              <span className="text-[color:var(--ink-3)]">·&nbsp;{m.age}</span>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
              </ul>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="ff-card p-4 md:p-5">
    <div className="label-tiny text-[color:var(--ink-2)]">{label}</div>
    <div className="font-display text-3xl md:text-4xl leading-none tracking-tighter mt-1.5">
      {value ?? 0}
    </div>
  </div>
);

const TIERS = [
  { key: "free", label: "Free · 4 members" },
  { key: "plus", label: "Plus · 6 members" },
  { key: "grand", label: "Grand · 10 members" },
  { key: "extended", label: "Extended Family · 20 members" },
];

function TierOverridePanel() {
  const [joinCode, setJoinCode] = useState("");
  const [family, setFamily] = useState(null);
  const [tier, setTier] = useState("extended");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const token = () => sessionStorage.getItem(TOKEN_KEY);

  const lookup = async (e) => {
    e.preventDefault();
    setMsg(null);
    setFamily(null);
    if (!joinCode.trim()) return;
    setLoading(true);
    try {
      const res = await api.adminFamilyLookup(token(), joinCode.trim());
      setFamily(res);
      setTier(res.effective_tier || "extended");
    } catch (err) {
      setMsg({
        tone: "error",
        text:
          err?.response?.status === 404
            ? "No family with that join code."
            : "Lookup failed.",
      });
    }
    setLoading(false);
  };

  const apply = async (applyTier) => {
    if (!family) return;
    setLoading(true);
    setMsg(null);
    try {
      const res = await api.adminOverrideTier(token(), {
        join_code: family.join_code,
        tier: applyTier,
        note: note || undefined,
      });
      setMsg({
        tone: "ok",
        text:
          applyTier === "clear"
            ? `Override cleared. Effective tier is now ${res.effective_tier}.`
            : `Upgraded to ${res.effective_tier} (${res.max_members} members).`,
      });
      // Refresh the family row so the card reflects the new state.
      const refreshed = await api.adminFamilyLookup(token(), family.join_code);
      setFamily(refreshed);
    } catch (err) {
      setMsg({
        tone: "error",
        text: err?.response?.data?.detail || "Override failed.",
      });
    }
    setLoading(false);
  };

  return (
    <section className="ff-card p-5 md:p-6" data-testid="admin-tier-override">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="label-tiny">Family tier override</div>
          <h2 className="font-display-italic text-2xl tracking-tight">
            Grant a family a bigger tier
          </h2>
        </div>
        <div className="label-tiny text-[color:var(--ink-3)]">
          bypasses Stripe · audit note recorded
        </div>
      </div>

      <form onSubmit={lookup} className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[200px]">
          <label className="label-tiny block mb-1" htmlFor="admin-join-code">Join code</label>
          <input
            id="admin-join-code"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            placeholder="JKABQP"
            maxLength={12}
            className="ff-input uppercase tracking-widest"
            data-testid="admin-tier-joincode"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !joinCode.trim()}
          className="ff-btn"
          data-testid="admin-tier-lookup"
        >
          {loading ? "Looking up…" : "Look up"}
        </button>
      </form>

      {family && (
        <div className="mt-5 space-y-4">
          <div className="ff-card p-4 bg-[color:var(--bg)]">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <div>
                <div className="font-display text-xl">{family.name || "—"}</div>
                <div className="label-tiny text-[color:var(--ink-2)] mt-1">
                  {family.join_code} · {family.member_count} members · effective tier:{" "}
                  <span className="text-[color:var(--ink)] font-medium">{family.effective_tier}</span>
                  {family.admin_tier_override && (
                    <> · <span className="text-[color:var(--ink)]">admin override active</span></>
                  )}
                  {family.stripe_subscription_status && (
                    <> · stripe: {family.stripe_subscription_status}</>
                  )}
                </div>
                <div className="label-tiny text-[color:var(--ink-3)] mt-1" data-testid="lookup-terms-status">
                  {family.terms_accepted_at
                    ? `T&C accepted: ${fmtTime(family.terms_accepted_at)}`
                    : "T&C: not recorded (created before checkbox was added)"}
                </div>
              </div>
              <div className="label-tiny text-[color:var(--ink-3)]">
                max today: <span className="text-[color:var(--ink)] font-medium">{family.max_members}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
            <div>
              <label className="label-tiny block mb-1" htmlFor="admin-tier-select">Tier to grant</label>
              <select
                id="admin-tier-select"
                value={tier}
                onChange={(e) => setTier(e.target.value)}
                className="ff-input"
                data-testid="admin-tier-select"
              >
                {TIERS.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-tiny block mb-1" htmlFor="admin-tier-note">
                Note (optional, stored on the family row)
              </label>
              <input
                id="admin-tier-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Founder account, beta tester, support case #123"
                maxLength={200}
                className="ff-input"
                data-testid="admin-tier-note"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => apply(tier)}
              disabled={loading}
              className="ff-btn ff-btn-primary"
              data-testid="admin-tier-apply"
            >
              {loading ? "Saving…" : `Grant ${tier}`}
            </button>
            {family.admin_tier_override && (
              <button
                onClick={() => apply("clear")}
                disabled={loading}
                className="ff-btn"
                data-testid="admin-tier-clear"
              >
                Clear override
              </button>
            )}
          </div>
        </div>
      )}

      {msg && (
        <div
          className={`mt-4 text-sm ${
            msg.tone === "error" ? "text-[color:var(--warn,#b14d2c)]" : "text-[color:var(--ink-2)]"
          }`}
          data-testid="admin-tier-msg"
        >
          {msg.text}
        </div>
      )}
    </section>
  );
}


/**
 * InactiveNudgePanel — admin re-engagement push. Dry-runs first
 * so the operator sees the audience before firing for real.
 * Minimum 3-day floor enforced server-side too (don't ping people
 * who used the app yesterday).
 */
function InactiveNudgePanel() {
  const [days, setDays] = useState(3);
  const [busy, setBusy] = useState(false);
  const [dryRun, setDryRun] = useState(null); // {target_count, families:[…]} | null
  const [msg, setMsg] = useState(null);

  const token = () => sessionStorage.getItem(TOKEN_KEY);

  const preview = async () => {
    setBusy(true);
    setMsg(null);
    setDryRun(null);
    try {
      const r = await api.adminInactiveNudge(token(), {
        min_inactive_days: Number(days),
        dry_run: true,
      });
      setDryRun(r);
    } catch (e) {
      setMsg({
        tone: "error",
        text: e?.response?.data?.detail || "Preview failed.",
      });
    } finally {
      setBusy(false);
    }
  };

  const fire = async () => {
    if (!dryRun) {
      setMsg({
        tone: "error",
        text: "Run Preview first so you can see who'll be pinged.",
      });
      return;
    }
    if (
      !window.confirm(
        `Send the re-engagement push to ${dryRun.target_count} family/families inactive ${days}+ days?`,
      )
    ) {
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.adminInactiveNudge(token(), {
        min_inactive_days: Number(days),
        dry_run: false,
      });
      setMsg({
        tone: "ok",
        text: `Pushed to ${r.families_pushed} family/families · ${r.pushes_delivered} device push${r.pushes_delivered === 1 ? "" : "es"} delivered.`,
      });
      setDryRun(null); // force a fresh preview before next fire
    } catch (e) {
      setMsg({
        tone: "error",
        text: e?.response?.data?.detail || "Push failed.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="ff-card p-5 md:p-6"
      data-testid="admin-inactive-nudge"
    >
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="label-tiny">Re-engagement push</div>
          <h2 className="font-display-italic text-2xl tracking-tight">
            Nudge inactive families
          </h2>
        </div>
      </div>
      <p className="text-[13px] text-[color:var(--ink-2)] leading-snug max-w-xl mb-4">
        Sends <em>"Don't forget your family on FamFeel — it's been a little
        while."</em> to every family whose last check-in / event is older
        than the chosen window. Preview first; the button below shows the
        target list before any push is sent.
      </p>
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <label className="flex flex-col gap-1">
          <span className="label-tiny">Inactive for at least</span>
          <div className="inline-flex items-center gap-2">
            <input
              type="number"
              min={3}
              max={90}
              value={days}
              onChange={(e) => {
                setDays(e.target.value);
                setDryRun(null);
              }}
              className="ff-input w-20"
              data-testid="admin-inactive-days"
            />
            <span className="text-[13px] text-[color:var(--ink-2)]">days</span>
          </div>
        </label>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={preview}
            disabled={busy}
            className="ff-btn"
            data-testid="admin-inactive-preview"
          >
            {busy ? "…" : "Preview audience"}
          </button>
          <button
            type="button"
            onClick={fire}
            disabled={busy || !dryRun || dryRun.target_count === 0}
            className="ff-btn ff-btn-primary"
            data-testid="admin-inactive-fire"
          >
            {dryRun ? `Push to ${dryRun.target_count}` : "Push"}
          </button>
        </div>
      </div>
      {dryRun && (
        <div className="mt-2 text-[13px] text-[color:var(--ink-2)]">
          <strong>{dryRun.target_count}</strong>{" "}
          family/families match.{" "}
          {dryRun.target_count > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer underline">
                Preview list ({Math.min(dryRun.target_count, 200)})
              </summary>
              <ul className="mt-2 space-y-1 max-h-64 overflow-y-auto">
                {(dryRun.families || []).map((f) => (
                  <li
                    key={f.id}
                    className="flex justify-between text-[12px] text-[color:var(--ink-3)]"
                  >
                    <span className="truncate mr-3">
                      {f.name} <span className="opacity-60">· {f.join_code}</span>
                    </span>
                    <span className="tabular shrink-0">
                      last seen{" "}
                      {f.last_active_at
                        ? new Date(f.last_active_at).toISOString().slice(0, 10)
                        : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
      {msg && (
        <div
          className={`mt-3 text-sm ${
            msg.tone === "error"
              ? "text-[color:var(--warn,#b14d2c)]"
              : "text-[color:var(--ink-2)]"
          }`}
          data-testid="admin-inactive-msg"
        >
          {msg.text}
        </div>
      )}
    </section>
  );
}


/**
 * FamiliesTable — every family on the system at a glance, with their
 * effective tier (manual overrides reflected) and self-reported
 * country. Powered by `/api/admin/families`. Read-only except for
 * delete (which reuses the parent's `deleteFamily` so the typed-code
 * confirmation prompt and refresh logic stay in one place).
 *
 * Sorted newest-first server-side. Renders compact on mobile and a
 * proper table on desktop. Country shows the flag emoji when we can
 * derive one from the ISO-2 code, otherwise the raw country string;
 * "?" stays "?" so the admin can spot families who didn't share.
 */
function FamiliesTable({ families, loading, onDelete, deleting }) {
  // One-shot cake-pushes — keyed by family id so two rows can fire
  // independently. We don't block on it (the request itself is fast)
  // but we disable the button while in-flight so a double-click can't
  // create two pushes back-to-back.
  const [pushingId, setPushingId] = useState(null);

  const forceBirthdayPush = async (familyId, name) => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) return;
    setPushingId(familyId);
    try {
      const res = await api.adminForceBirthdayPush(token, familyId);
      const names = res?.names || [];
      const sent = res?.pushes_sent ?? 0;
      if (names.length === 0) {
        toast(`No birthdays today in ${name}`, {
          description: "No member's MM-DD matches today UTC.",
        });
      } else {
        toast.success(`Birthday push fired for ${name}`, {
          description: `${names.join(", ")} · ${sent} device push${sent === 1 ? "" : "es"} delivered.`,
        });
      }
    } catch (e) {
      toast.error("Couldn't send birthday push", {
        description: e?.response?.data?.detail || e?.message || "Unknown error",
      });
    } finally {
      setPushingId(null);
    }
  };

  // ISO-2 → flag emoji. Mapping isn't exhaustive — anything not
  // matched falls back to plain text. Covers every country FamFeel
  // is currently active in plus the major EU/anglosphere markets we
  // expect to roll into next.
  const flagOf = (cc) => {
    if (!cc || cc === "?" || cc.length !== 2) return null;
    const up = cc.toUpperCase();
    // Codepoints A-Z → 0x1F1E6 + (charCode - 'A')
    return String.fromCodePoint(...[...up].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
  };

  const tierColor = (tier) => {
    switch (tier) {
      case "extended":
        return "bg-[#FFD2DD] text-[#5b1d31]";
      case "grand":
        return "bg-[#ECD5F5] text-[#3d2057]";
      case "plus":
        return "bg-[#CFE2F5] text-[#0f3a5c]";
      default:
        return "bg-[color:var(--bg-soft)] text-[color:var(--ink-2)]";
    }
  };

  return (
    <section data-testid="admin-families-table">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-display-italic text-2xl tracking-tight">All families</h2>
        <span className="label-tiny text-[color:var(--ink-3)]">
          {loading ? "Loading…" : `${(families || []).length} total`}
        </span>
      </div>

      <div className="rounded-[12px] border border-[color:var(--line)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--bg-soft)] text-[color:var(--ink-2)]">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Family</th>
                <th className="text-left px-3 py-2 font-medium">Code</th>
                <th className="text-left px-3 py-2 font-medium">Tier</th>
                <th className="text-left px-3 py-2 font-medium">Country</th>
                <th className="text-right px-3 py-2 font-medium">Members</th>
                <th className="text-left px-3 py-2 font-medium">Created</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {(families || []).map((f) => {
                const flag = flagOf(f.country);
                const created = f.created_at
                  ? new Date(f.created_at).toLocaleDateString(undefined, {
                      year: "2-digit",
                      month: "short",
                      day: "numeric",
                    })
                  : "—";
                return (
                  <tr
                    key={f.id}
                    className="border-t border-[color:var(--line-soft)]"
                    data-testid={`admin-family-row-${f.join_code}`}
                  >
                    <td className="px-3 py-2 font-medium">{f.name || "—"}</td>
                    <td className="px-3 py-2 tabular text-[color:var(--ink-2)]">{f.join_code}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] uppercase tracking-[0.06em] font-semibold ${tierColor(
                          f.effective_tier,
                        )}`}
                      >
                        {f.effective_tier}
                        {f.admin_tier_override && (
                          <span title="Manual admin override">·</span>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2 tabular">
                      {f.country
                        ? flag
                          ? `${flag} ${f.country}`
                          : f.country
                        : <span className="text-[color:var(--ink-3)]">?</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular">{f.member_count}</td>
                    <td className="px-3 py-2 tabular text-[color:var(--ink-2)]">{created}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-3 justify-end">
                        <button
                          type="button"
                          onClick={() => forceBirthdayPush(f.id, f.name || f.join_code)}
                          disabled={pushingId === f.id}
                          title="Fire happy-birthday push now"
                          aria-label={`Send birthday push for ${f.name || f.join_code}`}
                          className="hover:opacity-70 disabled:opacity-40 text-base leading-none"
                          data-testid={`admin-family-birthday-push-${f.join_code}`}
                        >
                          {pushingId === f.id ? "…" : "🎂"}
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(f.join_code, f.name || f.join_code)}
                          disabled={deleting === f.join_code}
                          className="text-[color:var(--rough-strong)] hover:underline disabled:opacity-50 text-xs"
                          data-testid={`admin-family-delete-${f.join_code}`}
                        >
                          {deleting === f.join_code ? "…" : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && (families || []).length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-[color:var(--ink-3)]">
                    No families yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}


/**
 * FeedbackInbox — shows notes submitted via Settings → Family → Send a
 * note. Newest first. Open / read / archived filter. Click a row to
 * mark-as-read. Archive drops it from the default view.
 */
function FeedbackInbox() {
  const [items, setItems] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [filter, setFilter] = useState("open"); // open | read | archived | all
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) return;
    setLoading(true);
    try {
      const { items: rows, open_count } = await api.adminFeedbackList(
        token,
        filter === "all" ? undefined : filter,
      );
      setItems(rows || []);
      setOpenCount(open_count || 0);
    } catch {
      /* silent — panel stays empty */
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, nextStatus) => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) return;
    setBusyId(id);
    try {
      await api.adminFeedbackUpdate(token, id, nextStatus);
      await load();
    } catch {
      /* ignore */
    } finally {
      setBusyId(null);
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-US", {
        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  return (
    <section
      className="rounded-2xl border border-[color:var(--line)] bg-[color:var(--surface)] p-5 md:p-6"
      data-testid="admin-feedback-panel"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="label-tiny mb-1">Feedback inbox</div>
          <div className="font-display-italic text-2xl tracking-tight">
            Notes from families
            {openCount > 0 && (
              <span
                className="ml-3 align-middle inline-flex items-center justify-center text-[12px] font-medium px-2 py-0.5 rounded-full bg-[color:var(--ink)] text-[color:var(--surface)]"
                data-testid="feedback-open-count"
              >
                {openCount} new
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-1.5" role="tablist" aria-label="Feedback filter">
          {["open", "read", "archived", "all"].map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={filter === v}
              onClick={() => setFilter(v)}
              className={`ff-chip ${filter === v ? "is-active" : ""}`}
              data-testid={`feedback-filter-${v}`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="label-tiny text-[color:var(--ink-3)]">Loading…</div>
      ) : items.length === 0 ? (
        <div
          className="text-[13px] text-[color:var(--ink-3)]"
          data-testid="feedback-empty"
        >
          No {filter === "all" ? "" : filter} notes yet.
        </div>
      ) : (
        <ul className="divide-y divide-[color:var(--line-soft)]">
          {items.map((n) => (
            <li
              key={n.id}
              className="py-4 flex flex-col gap-2"
              data-testid={`feedback-${n.id}`}
            >
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-medium">{n.family_name}</span>
                  <span className="label-tiny text-[color:var(--ink-3)]">
                    {n.join_code}
                    {n.member_name ? ` · ${n.member_name}` : ""}
                  </span>
                  <span className="label-tiny text-[color:var(--ink-3)]">
                    · {fmt(n.created_at)}
                  </span>
                  {n.status !== "open" && (
                    <span className="label-tiny text-[color:var(--ink-3)] uppercase tracking-[0.08em]">
                      · {n.status}
                    </span>
                  )}
                </div>
                <div className="flex gap-1.5">
                  {n.status === "open" && (
                    <button
                      type="button"
                      disabled={busyId === n.id}
                      onClick={() => setStatus(n.id, "read")}
                      className="ff-btn text-[12px]"
                      data-testid={`feedback-mark-read-${n.id}`}
                    >
                      Mark read
                    </button>
                  )}
                  {n.status !== "archived" ? (
                    <button
                      type="button"
                      disabled={busyId === n.id}
                      onClick={() => setStatus(n.id, "archived")}
                      className="ff-btn text-[12px]"
                      data-testid={`feedback-archive-${n.id}`}
                    >
                      Archive
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={busyId === n.id}
                      onClick={() => setStatus(n.id, "open")}
                      className="ff-btn text-[12px]"
                      data-testid={`feedback-unarchive-${n.id}`}
                    >
                      Unarchive
                    </button>
                  )}
                </div>
              </div>
              <div
                className="text-[14px] leading-snug whitespace-pre-wrap"
                data-testid={`feedback-body-${n.id}`}
              >
                {n.message}
              </div>
              {n.contact_email && (
                <a
                  href={`mailto:${n.contact_email}?subject=Re%3A%20your%20note%20to%20FamFeel`}
                  className="label-tiny text-[color:var(--ink-2)] hover:text-[color:var(--ink)] underline underline-offset-2 w-fit"
                  data-testid={`feedback-reply-${n.id}`}
                >
                  Reply to {n.contact_email}
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

